import { useState } from 'react'
import { Link } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiFehler, hole, schicke } from '../lib/api'
import { centsAusEingabe } from '../lib/geld'
import { useDarf } from '../session/SessionProvider'
import {
  Button,
  Card,
  Empty,
  IconButton,
  ListItem,
  Sheet,
  Switch,
  Symbol,
  TextField,
  useSnackbar,
  useConfirm,
} from '../ui'

interface LastRun {
  id: string
  automationId: string
  eventKey: string
  triggerEvent: string
  depth: number
  result: 'applied' | 'skipped' | 'failed'
  details: Record<string, unknown> | null
  error: string | null
  ranAt: string
}

interface Automation {
  id: string
  householdId: string
  createdByMemberId: string
  name: string
  triggerEvent: string
  conditions: Record<string, unknown>
  actions: Array<Record<string, unknown>>
  enabled: boolean
  createdAt: string
  updatedAt: string
  lastRun: LastRun | null
}

interface TemplateParam {
  key: string
  label: string
  type: 'cents' | 'text' | 'number'
  default: number | string
}

interface AutomationTemplate {
  key: string
  name: string
  description: string
  triggerEvent: string
  defaultConditions: Record<string, unknown>
  defaultActions: Array<Record<string, unknown>>
  params: TemplateParam[]
}

const zeitpunkt = (iso: string): string =>
  new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })

export function Automatisierungen() {
  const qc = useQueryClient()
  const zeigeMeldung = useSnackbar()
  const bestaetige = useConfirm()
  const darfVerwalten = useDarf('automation.manage')

  const [aktiveVorlage, setAktiveVorlage] = useState<AutomationTemplate | null>(null)
  const [neuerName, setNeuerName] = useState('')
  const [paramWerte, setParamWerte] = useState<Record<string, string>>({})
  const [verlaufId, setVerlaufId] = useState<string | null>(null)

  const { data, isPending } = useQuery({
    queryKey: ['automations'],
    queryFn: () => hole<{ automations: Automation[] }>('/api/automations'),
  })

  const { data: vorlagenData } = useQuery({
    queryKey: ['automations', 'templates'],
    queryFn: () => hole<{ templates: AutomationTemplate[] }>('/api/automations/templates'),
  })

  const { data: verlaufData, isPending: verlaufPending } = useQuery({
    queryKey: ['automations', verlaufId, 'runs'],
    queryFn: () => hole<{ runs: LastRun[] }>(`/api/automations/${verlaufId}/runs`),
    enabled: Boolean(verlaufId),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      schicke<{ automation: Automation }>('PATCH', `/api/automations/${id}`, { enabled }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['automations'] })
      zeigeMeldung(
        res.automation.enabled ? 'Automatisierung aktiviert' : 'Automatisierung pausiert',
        'info',
      )
    },
    onError: (err) => {
      const msg = err instanceof ApiFehler ? err.message : 'Fehler beim Ändern'
      zeigeMeldung(msg, 'error')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => schicke<unknown>('DELETE', `/api/automations/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automations'] })
      zeigeMeldung('Automatisierung gelöscht', 'info')
    },
    onError: (err) => {
      const msg = err instanceof ApiFehler ? err.message : 'Fehler beim Löschen'
      zeigeMeldung(msg, 'error')
    },
  })

  const createMutation = useMutation({
    mutationFn: (payload: {
      name: string
      triggerEvent: string
      conditions: Record<string, unknown>
      actions: Array<Record<string, unknown>>
      enabled: boolean
    }) => schicke<{ automation: Automation }>('POST', '/api/automations', payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['automations'] })
      setAktiveVorlage(null)
      zeigeMeldung('Automatisierung erfolgreich eingerichtet', 'erfolg')
    },
    onError: (err) => {
      const msg = err instanceof ApiFehler ? err.message : 'Fehler beim Einrichten'
      zeigeMeldung(msg, 'error')
    },
  })

  const regeln = data?.automations ?? []
  const vorlagen = vorlagenData?.templates ?? []

  const oeffneVorlage = (v: AutomationTemplate) => {
    setAktiveVorlage(v)
    setNeuerName(v.name)
    const initialParams: Record<string, string> = {}
    for (const p of v.params) {
      if (p.type === 'cents') {
        initialParams[p.key] = (Number(p.default) / 100).toFixed(2).replace('.', ',')
      } else {
        initialParams[p.key] = String(p.default)
      }
    }
    setParamWerte(initialParams)
  }

  const speichereVorlage = () => {
    if (!aktiveVorlage) return
    const name = neuerName.trim() || aktiveVorlage.name
    const actions = aktiveVorlage.defaultActions.map((act) => {
      const actionCopy = { ...act }
      if (aktiveVorlage.key === 'chore_bonus') {
        const cents = centsAusEingabe(paramWerte['amountCents'] ?? '0,50') ?? 50
        actionCopy.amountCents = cents
      }
      return actionCopy
    })

    createMutation.mutate({
      name,
      triggerEvent: aktiveVorlage.triggerEvent,
      conditions: aktiveVorlage.defaultConditions,
      actions,
      enabled: true,
    })
  }

  return (
    <>
      <div className="seitenkopf">
        <div>
          <h1>Automatisierungen</h1>
          <p>Einfache Wenn-Dann-Regeln für wiederkehrende Haushaltsabläufe.</p>
        </div>
        {darfVerwalten ? (
          <div>
            <Link
              to="/automatisierungen/editor"
              className="btn btn--filled"
              title="Neue Regel im Node-Editor erstellen"
            >
              <Symbol name="plus" width={18} height={18} />
              <span>Node-Editor</span>
            </Link>
          </div>
        ) : null}
      </div>

      <div className="stapel">
        <section aria-labelledby="regeln-titel">
          <h2 id="regeln-titel" className="typ-titel-md">
            Eingerichtete Regeln
          </h2>

          {isPending ? (
            <p className="unterzeile">Automatisierungen werden geladen …</p>
          ) : regeln.length === 0 ? (
            <Empty
              titel="Keine aktiven Automatisierungen"
              text="Wähle unten eine kuratierte Vorlage aus, um wiederkehrende Abläufe automatisch auszuführen."
            />
          ) : (
            <ul className="liste liste--gruppe" aria-label="Eingerichtete Automatisierungen">
              {regeln.map((r) => {
                const letzter = r.lastRun
                const statusPille = letzter ? (
                  <span
                    className={`automation-status-pille${
                      letzter.result === 'applied'
                        ? ' automation-status-pille--erfolg'
                        : letzter.result === 'failed'
                          ? ' automation-status-pille--fehler'
                          : ''
                    }`}
                  >
                    {letzter.result === 'applied' ? (
                      <Symbol name="check" width={14} height={14} />
                    ) : null}
                    {letzter.result === 'applied'
                      ? `Ausgeführt (${zeitpunkt(letzter.ranAt)})`
                      : letzter.result === 'skipped'
                        ? `Bedingung nicht erfüllt (${zeitpunkt(letzter.ranAt)})`
                        : `Fehlgeschlagen: ${letzter.error ?? 'Unbekannter Fehler'}`}
                  </span>
                ) : (
                  <span className="unterzeile">Noch kein Durchlauf</span>
                )

                return (
                  <li key={r.id}>
                    <ListItem
                      as="div"
                      titel={r.name}
                      unterzeile={
                        <div className="automation-zeile-unterzeile">
                          <span className="unterzeile">Auslöser: {r.triggerEvent}</span>
                          <div>{statusPille}</div>
                        </div>
                      }
                      folgend={
                        <div className="automation-zeile-aktionen">
                          <Switch
                            label={r.enabled ? 'Aktiv' : 'Pausiert'}
                            checked={r.enabled}
                            disabled={!darfVerwalten || toggleMutation.isPending}
                            onChange={(naechster) =>
                              toggleMutation.mutate({ id: r.id, enabled: naechster })
                            }
                          />
                          {darfVerwalten ? (
                            <Link
                              to={`/automatisierungen/editor/${r.id}`}
                              className="btn btn--text"
                              title="Regel im Node-Editor bearbeiten"
                            >
                              Editor
                            </Link>
                          ) : null}
                          <Button
                            variant="text"
                            onClick={() => setVerlaufId(r.id)}
                            title="Ausführungsverlauf anzeigen"
                          >
                            Verlauf
                          </Button>
                          {darfVerwalten ? (
                            <IconButton
                              name="trash"
                              label="Automatisierung löschen"
                              onClick={() => {
                                void bestaetige({
                                  titel: 'Automatisierung löschen?',
                                  text: `Soll „${r.name}“ wirklich gelöscht werden?`,
                                  bestaetigen: 'Löschen',
                                  gefahr: true,
                                }).then((bestaetigt) => {
                                  if (bestaetigt) deleteMutation.mutate(r.id)
                                })
                              }}
                            />
                          ) : null}
                        </div>
                      }
                    />
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <section aria-labelledby="vorlagen-titel">
          <h2 id="vorlagen-titel" className="typ-titel-md">
            Verfügbare Vorlagen
          </h2>
          <div className="automation-vorlagen-raster">
            {vorlagen.map((v) => (
              <Card key={v.key} titel={v.name}>
                <p className="automation-karte-text">{v.description}</p>
                <div className="automation-karte-aktionen">
                  <Button
                    variant="tonal"
                    disabled={!darfVerwalten}
                    onClick={() => oeffneVorlage(v)}
                  >
                    Einrichten
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </section>
      </div>

      {/* Sheet zum Konfigurieren und Einrichten einer Vorlage */}
      <Sheet
        offen={Boolean(aktiveVorlage)}
        titel={aktiveVorlage ? `${aktiveVorlage.name} einrichten` : ''}
        onSchliessen={() => setAktiveVorlage(null)}
      >
        {aktiveVorlage ? (
          <form
            className="stapel"
            onSubmit={(e) => {
              e.preventDefault()
              speichereVorlage()
            }}
          >
            <p className="unterzeile">{aktiveVorlage.description}</p>
            <TextField
              id="automation-name"
              label="Bezeichnung der Regel"
              wert={neuerName}
              onWert={(w) => setNeuerName(w)}
              required
            />

            {aktiveVorlage.params.map((p) => (
              <TextField
                key={p.key}
                id={`automation-param-${p.key}`}
                label={p.label}
                wert={paramWerte[p.key] ?? ''}
                onWert={(w) => setParamWerte((alt) => ({ ...alt, [p.key]: w }))}
                required
              />
            ))}

            <div className="automation-form-aktionen">
              <Button variant="text" onClick={() => setAktiveVorlage(null)} type="button">
                Abbrechen
              </Button>
              <Button variant="filled" type="submit" disabled={createMutation.isPending}>
                Regel aktivieren
              </Button>
            </div>
          </form>
        ) : null}
      </Sheet>

      {/* Sheet für den Ausführungsverlauf */}
      <Sheet
        offen={Boolean(verlaufId)}
        titel="Ausführungsverlauf"
        onSchliessen={() => setVerlaufId(null)}
      >
        <div className="stapel">
          {verlaufPending ? (
            <p className="unterzeile">Verlauf wird geladen …</p>
          ) : !verlaufData || verlaufData.runs.length === 0 ? (
            <Empty
              titel="Bisher kein Verlauf vorhanden"
              text="Diese Regel wurde noch nicht von einem Ereignis ausgelöst."
            />
          ) : (
            <ul className="liste liste--gruppe" aria-label="Protokoll der letzten Ausführungen">
              {verlaufData.runs.map((lauf) => (
                <li key={lauf.id}>
                  <ListItem
                    as="div"
                    titel={
                      lauf.result === 'applied'
                        ? 'Erfolgreich ausgeführt'
                        : lauf.result === 'skipped'
                          ? 'Bedingung nicht erfüllt (übersprungen)'
                          : 'Fehlgeschlagen'
                    }
                    unterzeile={
                      <div className="automation-verlauf-zeile">
                        <span className="unterzeile">
                          {zeitpunkt(lauf.ranAt)} • Ereignis: {lauf.triggerEvent}
                        </span>
                        {lauf.error ? (
                          <span className="automation-verlauf-fehler">{lauf.error}</span>
                        ) : null}
                      </div>
                    }
                    fuehrend={
                      <Symbol
                        name={lauf.result === 'applied' ? 'check' : 'clock'}
                        className={
                          lauf.result === 'applied'
                            ? 'automation-symbol--erfolg'
                            : lauf.result === 'failed'
                              ? 'automation-symbol--fehler'
                              : 'automation-symbol--neutral'
                        }
                      />
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </Sheet>
    </>
  )
}
